import { notificationApi } from '@/api/notifications'
import { useNotificationStore } from '@/store/notifications'
import type { NotificationQuery } from '@/types/notification'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

export const notificationKeys = {
  all: ['notifications'] as const,
  list: (query?: NotificationQuery) => [...notificationKeys.all, 'list', query] as const,
  detail: (id: string) => [...notificationKeys.all, 'detail', id] as const,
  unreadCount: () => [...notificationKeys.all, 'unread-count'] as const,
  channelStatus: () => [...notificationKeys.all, 'channel-status'] as const,
  telegram: () => [...notificationKeys.all, 'telegram'] as const,
}

/** Fetch paginated notification list */
export function useNotificationList(query?: NotificationQuery) {
  return useQuery({
    queryKey: notificationKeys.list(query),
    queryFn: () => notificationApi.list(query),
    staleTime: 30_000,
  })
}

/** Fetch unread notification count */
export function useUnreadNotificationCount() {
  const setUnreadCount = useNotificationStore((s) => s.setUnreadCount)

  return useQuery({
    queryKey: notificationKeys.unreadCount(),
    queryFn: async () => {
      const resp = await notificationApi.unreadCount()
      setUnreadCount(resp.count)
      return resp
    },
    refetchInterval: 30_000,
    staleTime: 15_000,
  })
}

/** Mark a single notification as read */
export function useMarkNotificationRead() {
  const queryClient = useQueryClient()
  const markRead = useNotificationStore((s) => s.markRead)

  return useMutation({
    mutationFn: (id: string) => notificationApi.markRead(id),
    onSuccess: (_data, id) => {
      markRead(id)
      queryClient.invalidateQueries({ queryKey: notificationKeys.all })
    },
  })
}

/** Mark all notifications as read */
export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient()
  const markAllRead = useNotificationStore((s) => s.markAllRead)

  return useMutation({
    mutationFn: () => notificationApi.markAllRead(),
    onSuccess: () => {
      markAllRead()
      queryClient.invalidateQueries({ queryKey: notificationKeys.all })
    },
  })
}

/** Delete a single notification */
export function useDeleteNotification() {
  const queryClient = useQueryClient()
  const remove = useNotificationStore((s) => s.remove)

  return useMutation({
    mutationFn: (id: string) => notificationApi.delete(id),
    onSuccess: (_data, id) => {
      remove(id)
      queryClient.invalidateQueries({ queryKey: notificationKeys.all })
      toast.success('Notification deleted')
    },
    onError: () => {
      toast.error('Failed to delete notification')
    },
  })
}

/** Bulk delete notifications */
export function useBulkDeleteNotifications() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (ids: string[]) => notificationApi.bulkDelete({ ids }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all })
      toast.success('Notifications deleted')
    },
    onError: () => {
      toast.error('Failed to delete notifications')
    },
  })
}

/** Get Telegram link status */
export function useTelegramStatus() {
  return useQuery({
    queryKey: notificationKeys.telegram(),
    queryFn: () => notificationApi.telegramStatus(),
  })
}

/** Get channel delivery status */
export function useChannelStatus() {
  return useQuery({
    queryKey: notificationKeys.channelStatus(),
    queryFn: () => notificationApi.channelStatus(),
  })
}
